// KeyValue (job page round): labelled facts on hairlines (a job's Details, a form's answers, what's at an
// address, a hover card's fields). grid: the label muted on the left, its value beside it; spread: the
// thing in ink on the left, its value muted on the right ("Furnace · 2016"). A fact that is a problem has
// StateMark's red bar before its value, the words in ink (DESIGN.md §2: red is a mark, never text). The board's hover card and side panel take it in place of their
// stand-in, with the same props. Facts side by side, the figure large (truck stock's), are Figures: the
// figures layout folded into it (BL-189). Reads only --kv-*.
// Rows (BL-189, Sales tax's returns): figures one under another, the label muted on the left, the
// amount on the right in tabular figures, bold; no hairlines. A row's tone makes the total strong (its
// label in ink, bold) or a by-the-way figure muted ("Rounding on the lines").
// Grid's label column (BL-189) sizes to its longest label, from --kv-label-w up to --kv-label-max, and a
// label wraps only past the cap; on a phone it stays --kv-label-w. Groups (a month's targets, then what's
// budgeted each day) share that one column, each under its own subhead on a hairline.
import type { ReactNode } from 'react';
import { StateMark } from '../StateMark/StateMark.tsx';
import './KeyValue.css';

export interface KeyValueItem {
  label: ReactNode;
  value: ReactNode;
  /** A problem: StateMark's red bar before the value; the words stay ink. */
  bad?: boolean;
  /** A muted line under the value. */
  sub?: ReactNode;
  /** Rows: strong, the total (its label in ink, bold); muted, a by-the-way figure (its value muted). */
  tone?: 'strong' | 'muted';
}

export interface KeyValueProps {
  items: KeyValueItem[];
  /** Tight rows, for a hover card. */
  density?: 'normal' | 'tight';
  /**
   * grid: label beside value; spread: a thing and its value; rows: amounts one under another, the label
   * left and the amount right-aligned. Facts side by side are Figures.
   */
  layout?: 'grid' | 'spread' | 'rows';
  /** Its name for screen readers, when nothing above it names it. */
  label?: string;
  /** Grid: at 600 px or narrower each label sits above its value (a read-only sheet's facts). */
  isStackedOnPhone?: boolean;
  /** Grid: more facts after the items, each group under its subhead, all sharing one label column. */
  groups?: readonly KeyValueGroup[];
}

/** Grid: a group of facts under its subhead ("Budgeted each working day"). */
export interface KeyValueGroup {
  head: ReactNode;
  items: KeyValueItem[];
}

function Rows({ items }: { items: KeyValueItem[] }) {
  return (
    <>
      {items.map((it, i) => (
        <div key={i} className="fl-kv__row" data-bad={it.bad ? '' : undefined} data-tone={it.tone}>
          <dt className="fl-kv__label">{it.label}</dt>
          <dd className="fl-kv__value">
            {it.bad ? <StateMark state="problem" /> : null}
            {it.value}
            {it.sub != null ? <span className="fl-kv__sub">{it.sub}</span> : null}
          </dd>
        </div>
      ))}
    </>
  );
}

export function KeyValue({
  items,
  density = 'normal',
  layout = 'grid',
  label,
  isStackedOnPhone,
  groups,
}: KeyValueProps) {
  const data = {
    'data-density': density,
    'data-layout': layout,
    'data-stacked-on-phone': isStackedOnPhone ? '' : undefined,
  };
  if (layout === 'grid' && groups?.length)
    // a dl holds only its facts, so the groups are dls in one grid that owns the label column
    return (
      <div className="fl-kv" {...data} role={label ? 'group' : undefined} aria-label={label}>
        <dl className="fl-kv__set">
          <Rows items={items} />
        </dl>
        {groups.map((g, i) => (
          <div key={i} className="fl-kv__group">
            <h3 className="fl-kv__head">{g.head}</h3>
            <dl className="fl-kv__set">
              <Rows items={g.items} />
            </dl>
          </div>
        ))}
      </div>
    );
  return (
    <dl className="fl-kv" {...data} aria-label={label}>
      <Rows items={items} />
    </dl>
  );
}
